const FP_PURCHASE_SUMMARY_SELECTION_EVENTS = [
  'fp:cordinha-change',
  'fp:acabamento-change',
  'fp:motorizacao-change',
  'fp:connected-product-swapped',
];

class FpProductPurchaseSummary extends HTMLElement {
  connectedCallback() {
    if (this.dataset.fpSummaryInit === 'true') return;

    this.dataset.fpSummaryInit = 'true';
    this.section = this.closest('.shopify-section') || document;
    this.variantsById = this.readVariantMap();
    this.variant = this.getVariantById(this.dataset.initialVariantId) || this.readInitialVariant();
    this.onVariantUpdate = this.handleVariantUpdate.bind(this);
    this.onVariantResolved = this.handleVariantResolved.bind(this);
    this.onDimensionChange = this.handleDimensionChange.bind(this);
    this.onInteractionIntent = this.handleInteractionIntent.bind(this);
    this.onNativeChangeIntent = this.handleNativeChangeIntent.bind(this);
    this.onSelectionSignal = this.handleSelectionSignal.bind(this);
    this.onDawnVariantChange = this.handleDawnVariantChange.bind(this);
    this.refreshFrame = 0;
    this.loadingTimer = 0;
    this.loadingSafetyTimer = 0;
    this.loadingStartedAt = 0;
    this.pendingVariant = null;
    this.pendingShouldRender = false;
    this.displayedVariant = null;
    this.externalSignalsReadyAt = this.now() + 600;

    document.addEventListener('variant:update', this.onVariantUpdate);
    document.addEventListener('fp:sobmedida:variant-resolved', this.onVariantResolved);
    document.addEventListener('dimension-change', this.onDimensionChange);
    FP_PURCHASE_SUMMARY_SELECTION_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, this.onSelectionSignal);
    });
    this.subscribeToDawnVariantChanges();

    this.section.addEventListener('click', this.onInteractionIntent, true);
    this.section.addEventListener('keydown', this.onInteractionIntent, true);
    this.section.addEventListener('change', this.onNativeChangeIntent, true);

    this.refresh();
    window.setTimeout(() => this.refresh(), 150);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.onVariantUpdate);
    document.removeEventListener('fp:sobmedida:variant-resolved', this.onVariantResolved);
    document.removeEventListener('dimension-change', this.onDimensionChange);
    FP_PURCHASE_SUMMARY_SELECTION_EVENTS.forEach((eventName) => {
      document.removeEventListener(eventName, this.onSelectionSignal);
    });

    if (this.dawnVariantUnsubscriber) {
      this.dawnVariantUnsubscriber();
      this.dawnVariantUnsubscriber = null;
    }

    this.section.removeEventListener('click', this.onInteractionIntent, true);
    this.section.removeEventListener('keydown', this.onInteractionIntent, true);
    this.section.removeEventListener('change', this.onNativeChangeIntent, true);

    if (this.refreshFrame) {
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = 0;
    }

    this.clearLoadingTimer();
    this.clearLoadingSafetyTimer();
  }

  readInitialVariant() {
    return {
      id: String(this.dataset.initialVariantId || ''),
      price: this.parseCents(this.dataset.initialPrice),
      compare_at_price: this.parseCents(this.dataset.initialCompareAtPrice),
      available: this.dataset.initialAvailable !== 'false',
    };
  }

  readVariantMap() {
    const map = new Map();
    const source = this.querySelector('[data-fp-summary-variants]');

    if (source?.textContent) {
      this.addVariantsFromJson(map, source.textContent);
    }

    this.addSobmedidaVariants(map);
    this.addLiveCollectionVariants(map);

    return map;
  }

  addVariantsFromJson(map, textContent) {
    try {
      const variants = JSON.parse(textContent);

      if (!Array.isArray(variants)) return;

      variants.forEach((variant) => this.addVariantToMap(map, variant));
    } catch (error) {
      this.debugWarn('Invalid variant map', error);
    }
  }

  addSobmedidaVariants(map) {
    if (!(this.section instanceof Element)) return;

    this.section
      .querySelectorAll('script[data-fp-sobmedida-variant-json], script[data-fp-sobmedida-legacy-json]')
      .forEach((source) => {
        if (!source.textContent) return;

        try {
          const payload = JSON.parse(source.textContent);

          if (Array.isArray(payload)) {
            payload.forEach((variant) => this.addVariantToMap(map, variant));
            return;
          }

          if (payload?.id && payload?.price != null) {
            this.addVariantToMap(map, payload);
          }

          if (Array.isArray(payload?.products)) {
            payload.products.forEach((product) => {
              const variants = Array.isArray(product?.variants) ? product.variants : [];
              variants.forEach((variant) => this.addVariantToMap(map, variant));
            });
          }
        } catch (error) {
          this.debugWarn('Invalid sobmedida variant payload', error);
        }
      });
  }

  addLiveCollectionVariants(map) {
    const products = Array.isArray(window.productsCollection) ? window.productsCollection : [];

    products.forEach((product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      variants.forEach((variant) => this.addVariantToMap(map, variant));
    });
  }

  subscribeToDawnVariantChanges() {
    const subscribeFn = window.subscribe || (typeof subscribe !== 'undefined' ? subscribe : null);
    const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

    if (typeof subscribeFn !== 'function' || !pubSubEvents?.variantChange) return;

    try {
      this.dawnVariantUnsubscriber = subscribeFn(pubSubEvents.variantChange, this.onDawnVariantChange);
    } catch (error) {
      this.debugWarn('Could not subscribe to variant changes', error);
    }
  }

  addVariantToMap(map, variant) {
    if (!variant?.id) return;

    const normalizedVariant = this.normalizeVariant(variant);
    if (normalizedVariant) map.set(normalizedVariant.id, normalizedVariant);
  }

  normalizeVariant(variant) {
    if (!variant?.id) return null;

    return {
      id: String(variant.id),
      price: this.parseCents(variant.price),
      compare_at_price: this.parseCents(variant.compare_at_price),
      available: variant.available !== false,
    };
  }

  parseCents(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  debugWarn(message, error) {
    if (this.dataset.fpDebug !== 'true') return;

    // Keep debug output in the console only when the block is explicitly configured for it.
    console.warn(`[fp-product-purchase-summary] ${message}`, error);
  }

  now() {
    return window.performance?.now?.() || Date.now();
  }

  handleInteractionIntent(event) {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target instanceof Element ? event.target : null;
    const intent = this.resolveInteractionIntent(target);

    if (!intent) return;

    this.beginLoading({ allowFallbackCompletion: intent.allowFallbackCompletion });
  }

  handleNativeChangeIntent(event) {
    const target = event.target instanceof Element ? event.target : null;
    const intent = this.resolveChangeIntent(target);

    if (!intent) return;

    this.beginLoading({ allowFallbackCompletion: false });
    this.scheduleRefresh();
    window.setTimeout(() => this.scheduleRefresh(), 80);
    window.setTimeout(() => this.scheduleRefresh(), 260);
  }

  handleSelectionSignal(event) {
    if (!this.eventBelongsToSection(event)) return;

    const isInitialSelectorSync = this.now() < this.externalSignalsReadyAt && !this.isLoading();
    if (isInitialSelectorSync) return;

    this.beginLoading({ allowFallbackCompletion: true });
  }

  resolveInteractionIntent(target) {
    if (!target || !(this.section instanceof Element) || !this.section.contains(target)) return null;

    const cordinhaCard = target.closest('[data-fp-cordinha-card]');
    if (cordinhaCard) {
      if (cordinhaCard.getAttribute('aria-checked') === 'true') return null;
      return { allowFallbackCompletion: true };
    }

    const acabamentoItem = target.closest('[data-fp-acabamento-produto-url]');
    if (acabamentoItem) {
      if (acabamentoItem.dataset.fpAcabamentoProdutoCurrent === 'true') return null;
      if (acabamentoItem.dataset.fpAcabamentoProdutoAvailable === 'false') return null;
      return { allowFallbackCompletion: true };
    }

    const motorizacaoItem = target.closest('[data-fp-motorizacao-product-url]');
    if (motorizacaoItem) {
      if (motorizacaoItem.dataset.fpMotorizacaoCurrent === 'true') return null;
      if (motorizacaoItem.dataset.fpMotorizacaoAvailable === 'false') return null;
      return { allowFallbackCompletion: true };
    }

    const liveDimensionControl = target.closest('variant-width-height select, .container-product-variant-width-height select, #select-width, #select-height');
    if (liveDimensionControl) return { allowFallbackCompletion: false };

    const variantLabel = target.closest('variant-picker .variant-option__button-label');
    if (variantLabel) {
      if (variantLabel.querySelector(':checked')) return null;
      if (variantLabel.querySelector('[disabled], [aria-disabled="true"], [data-option-available="false"]')) return null;
      return { allowFallbackCompletion: false };
    }

    const dawnVariantChoice = target.closest('variant-selects label, variant-selects select, variant-radios label, variant-radios input');
    if (dawnVariantChoice) {
      if (dawnVariantChoice.matches('[disabled], [aria-disabled="true"]')) return null;
      return { allowFallbackCompletion: false };
    }

    return null;
  }

  resolveChangeIntent(target) {
    if (!target || !(this.section instanceof Element) || !this.section.contains(target)) return null;

    if (target.matches('.fp-sobmedida-dimensions__select, [data-fp-sobmedida-width], [data-fp-sobmedida-height]')) {
      return { allowFallbackCompletion: false };
    }

    if (target.matches('#select-width, #select-height, [name="select-width"], [name="select-height"]')) {
      return { allowFallbackCompletion: false };
    }

    if (target.closest('variant-width-height') && target.matches('select, input[type="radio"]')) {
      return { allowFallbackCompletion: false };
    }

    if (target.closest('variant-picker, variant-selects, variant-radios') && target.matches('select, input[type="radio"]')) {
      return { allowFallbackCompletion: false };
    }

    return null;
  }

  eventBelongsToSection(event) {
    const detail = event.detail || {};
    const sectionId = detail.sectionId ? String(detail.sectionId) : '';
    const ownSectionId = String(this.dataset.sectionId || '');

    if (sectionId && ownSectionId) return sectionId === ownSectionId;

    return (
      event.target instanceof Element &&
      this.section instanceof Element &&
      this.section.contains(event.target)
    );
  }

  getVariantById(id) {
    const variantId = String(id || '');

    if (!variantId) return null;
    if (this.variantsById?.has(variantId)) return this.variantsById.get(variantId);
    if (String(this.variant?.id || '') === variantId) return this.variant;

    return null;
  }

  handleVariantUpdate(event) {
    const detail = event.detail || {};
    const productId = detail.data?.productId ? String(detail.data.productId) : '';
    const ownProductId = String(this.dataset.productId || '');

    if (productId && ownProductId && productId !== ownProductId) return;

    if (
      !productId &&
      event.target instanceof Element &&
      this.section instanceof Element &&
      !this.section.contains(event.target)
    ) {
      return;
    }

    const nextVariant = this.normalizeVariant(detail.resource);
    if (!nextVariant?.id) {
      this.scheduleRefresh();
      return;
    }

    this.variantsById.set(nextVariant.id, nextVariant);
    this.handleResolvedVariant(nextVariant);
  }

  handleDawnVariantChange(event) {
    const data = event?.data || event?.detail?.data || event?.detail || event || {};
    const sectionId = data.sectionId ? String(data.sectionId) : '';
    const ownSectionId = String(this.dataset.sectionId || '');

    if (sectionId && ownSectionId && sectionId !== ownSectionId) return;

    const nextVariant = this.normalizeVariant(data.variant);
    if (!nextVariant?.id) {
      this.scheduleRefresh();
      return;
    }

    this.variantsById.set(nextVariant.id, nextVariant);
    this.handleResolvedVariant(nextVariant);
  }

  handleVariantResolved(event) {
    const detail = event.detail || {};
    const sectionId = detail.sectionId ? String(detail.sectionId) : '';
    const ownSectionId = String(this.dataset.sectionId || '');

    if (sectionId && ownSectionId && sectionId !== ownSectionId) return;

    const nextVariant = this.normalizeVariant(detail.variant);
    if (!nextVariant?.id) {
      this.scheduleRefresh();
      return;
    }

    this.variantsById.set(nextVariant.id, nextVariant);
    this.handleResolvedVariant(nextVariant);
  }

  handleResolvedVariant(nextVariant) {
    const shouldRender = this.hasPriceChange(this.displayedVariant || this.variant, nextVariant);

    if (shouldRender && !this.isLoading()) {
      this.beginLoading();
    }

    if (this.isLoading()) {
      this.pendingVariant = nextVariant;
      this.pendingShouldRender = shouldRender;
      this.scheduleLoadingCompletion();
      return;
    }

    this.variant = nextVariant;
    if (shouldRender || !this.displayedVariant) {
      this.renderVariant(nextVariant);
    } else {
      this.displayedVariant = nextVariant;
    }
    this.updateDebug(this.readState(), 'visible');
  }

  handleDimensionChange(event) {
    const detail = event.detail || {};
    const sectionId = detail.sectionId ? String(detail.sectionId) : '';
    const ownSectionId = String(this.dataset.sectionId || '');

    if (sectionId && ownSectionId && sectionId !== ownSectionId) return;

    const state = this.readState();

    if (!state.hasValidDimensions) {
      this.stopLoading();
      this.scheduleRefresh();
      return;
    }

    if (!this.hidden && this.displayedVariant?.id) {
      this.beginLoading({ allowFallbackCompletion: false });
    }

    this.scheduleRefresh();
  }

  scheduleRefresh() {
    if (this.refreshFrame) window.cancelAnimationFrame(this.refreshFrame);

    this.refreshFrame = window.requestAnimationFrame(() => {
      this.refreshFrame = 0;
      this.refresh();
    });
  }

  refresh() {
    this.addLiveCollectionVariants(this.variantsById);

    const state = this.readState();
    const activeVariant = this.getVariantById(state.variantId) || this.pendingVariant;

    if (this.isLoading() && this.displayedVariant?.id) {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');

      if (state.hasValidDimensions && activeVariant?.id) {
        this.pendingVariant = activeVariant;
        this.pendingShouldRender = this.hasPriceChange(this.displayedVariant || this.variant, activeVariant);
        this.scheduleLoadingCompletion();
      }

      this.updateDebug(state, 'loading');
      return;
    }

    const canRender =
      state.hasValidDimensions &&
      Boolean(activeVariant?.id) &&
      Number.isFinite(Number(activeVariant.price));

    this.hidden = !canRender;
    this.setAttribute('aria-hidden', canRender ? 'false' : 'true');

    if (!canRender) {
      this.stopLoading();
      this.updateDebug(state, 'hidden');
      return;
    }

    this.renderVariant(activeVariant);
    this.updateDebug(state, 'visible');
  }

  readState() {
    const variantInput = this.findPrimaryVariantInput();
    const widthInput = this.find('[data-fp-sobmedida-prop-width-raw], input[name="properties[_largura]"], input[name="properties[Largura]"], .propertie-width, #select-width, [name="select-width"]');
    const heightInput = this.find('[data-fp-sobmedida-prop-height-raw], input[name="properties[_altura]"], input[name="properties[Altura]"], .propertie-height, #select-height, [name="select-height"]');
    const requiresDimensions = Boolean(this.find('variant-width-height, .container-product-variant-width-height, #select-width, #select-height, [data-fp-sobmedida-width], [data-fp-sobmedida-height]'));
    const widthValue = Number(widthInput?.value || 0);
    const heightValue = Number(heightInput?.value || 0);
    const widthReady = Boolean(widthInput && !widthInput.disabled && widthValue > 0);
    const heightReady = Boolean(heightInput && !heightInput.disabled && heightValue > 0);

    return {
      variantId: variantInput?.value || '',
      widthReady,
      heightReady,
      hasValidDimensions: requiresDimensions ? widthReady && heightReady : true,
    };
  }

  findPrimaryVariantInput() {
    const preferredSelectors = [
      '.product-variant-id',
      'input[ref="variantId"]',
    ];

    for (const selector of preferredSelectors) {
      const input = this.find(selector);
      if (input) return input;
    }

    const sectionId = String(this.dataset.sectionId || '');
    const escapedSectionId = this.escapeCssIdentifier(sectionId);
    const formInput = escapedSectionId ? this.find(`#product-form-${escapedSectionId} input[name="id"]`) : null;
    if (formInput) return formInput;

    return this.find('input[name="id"]');
  }

  find(selector) {
    return this.section.querySelector(selector);
  }

  escapeCssIdentifier(value) {
    if (!value) return '';
    if (window.CSS?.escape) return window.CSS.escape(value);

    return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  renderVariant(variant) {
    this.variant = variant;
    this.displayedVariant = variant;
    this.renderSummary(variant);
  }

  renderSummary(variant = this.variant) {
    const price = this.parseCents(variant.price);
    const compareAt = this.parseCents(variant.compare_at_price);
    const showDiscount = this.dataset.showCompareAtDiscount === 'true' && compareAt > price;
    const discount = Math.max(0, compareAt - price);
    const unitPrice = showDiscount ? compareAt : price;
    const total = price;
    const hideSubtotalWhenEqualsTotal = this.dataset.hideSubtotalWhenEqualsTotal === 'true';
    const showSubtotal = showDiscount && !(hideSubtotalWhenEqualsTotal && price === total);

    this.setText('[data-fp-summary-unit-price]', this.formatMoney(unitPrice));
    this.setText('[data-fp-summary-subtotal]', this.formatMoney(price));
    this.setText('[data-fp-summary-total]', this.formatMoney(total));
    this.setText('[data-fp-summary-discount]', this.formatSignedMoney(discount));

    this.toggleClass('[data-fp-summary-unit-price]', 'fp-product-purchase-summary__value--compare', showDiscount);
    this.toggle('[data-fp-summary-discount-row]', showDiscount);
    this.toggle('[data-fp-summary-subtotal-row]', showSubtotal);
    this.toggle('[data-fp-summary-coupon-row]', false);
  }

  hasPriceChange(currentVariant, nextVariant) {
    if (!currentVariant?.id || !nextVariant?.id) return true;

    return this.priceSignature(currentVariant) !== this.priceSignature(nextVariant);
  }

  priceSignature(variant) {
    return [
      this.parseCents(variant?.price),
      this.parseCents(variant?.compare_at_price),
    ].join(':');
  }

  isLoading() {
    return this.loadingStartedAt > 0;
  }

  beginLoading(options = {}) {
    const { allowFallbackCompletion = false } = options;

    if (!this.canShowLoading()) return;

    if (this.isLoading()) {
      if (allowFallbackCompletion && !this.pendingVariant) this.scheduleLoadingCompletion();
      return;
    }

    this.lockPlaceholderSize();
    this.loadingStartedAt = this.now();
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    this.toggle('[data-fp-summary-loading]', true);
    this.startLoadingSafetyTimer();

    if (allowFallbackCompletion) this.scheduleLoadingCompletion();
  }

  scheduleLoadingCompletion() {
    if (!this.isLoading()) return;

    this.clearLoadingTimer();

    const minDuration = this.getLoadingMinDuration();
    const now = this.now();
    const elapsed = now - this.loadingStartedAt;
    const delay = Math.max(0, minDuration - elapsed);

    this.loadingTimer = window.setTimeout(() => {
      this.loadingTimer = 0;
      this.finishLoading();
    }, delay);
  }

  finishLoading() {
    const nextVariant = this.pendingVariant;
    const shouldRender = this.pendingShouldRender || !this.displayedVariant;

    this.stopLoading({ clearPending: false });

    if (nextVariant?.id) {
      this.variant = nextVariant;
      if (shouldRender) {
        this.renderVariant(nextVariant);
      } else {
        this.displayedVariant = nextVariant;
      }
    }

    this.pendingVariant = null;
    this.pendingShouldRender = false;
    this.updateDebug(this.readState(), 'visible');
  }

  stopLoading(options = {}) {
    const { clearPending = true } = options;

    this.clearLoadingTimer();
    this.clearLoadingSafetyTimer();
    this.loadingStartedAt = 0;
    this.classList.remove('is-loading');
    this.setAttribute('aria-busy', 'false');
    this.toggle('[data-fp-summary-loading]', false);
    this.unlockPlaceholderSize();

    if (clearPending) {
      this.pendingVariant = null;
      this.pendingShouldRender = false;
    }
  }

  canShowLoading() {
    const state = this.readState();

    return !this.hidden && state.hasValidDimensions && Boolean(this.displayedVariant?.id || this.variant?.id);
  }

  lockPlaceholderSize() {
    const inner = this.querySelector('.fp-product-purchase-summary__inner');
    if (!(inner instanceof HTMLElement)) return;

    const height = Math.ceil(inner.getBoundingClientRect().height);
    if (height > 0) this.style.setProperty('--fp-summary-placeholder-height', `${height}px`);
  }

  unlockPlaceholderSize() {
    this.style.removeProperty('--fp-summary-placeholder-height');
  }

  clearLoadingTimer() {
    if (!this.loadingTimer) return;

    window.clearTimeout(this.loadingTimer);
    this.loadingTimer = 0;
  }

  startLoadingSafetyTimer() {
    this.clearLoadingSafetyTimer();

    this.loadingSafetyTimer = window.setTimeout(() => {
      this.loadingSafetyTimer = 0;
      this.finishLoading();
      this.scheduleRefresh();
    }, this.getLoadingMaxDuration());
  }

  clearLoadingSafetyTimer() {
    if (!this.loadingSafetyTimer) return;

    window.clearTimeout(this.loadingSafetyTimer);
    this.loadingSafetyTimer = 0;
  }

  getLoadingMinDuration() {
    const parsed = Number(this.dataset.loadingMinDurationMs);
    if (!Number.isFinite(parsed)) return 1000;

    return Math.max(0, parsed);
  }

  getLoadingMaxDuration() {
    return Math.max(this.getLoadingMinDuration(), 4000);
  }

  setText(selector, value) {
    const target = this.querySelector(selector);
    if (target) target.textContent = value;
  }

  toggle(selector, isVisible) {
    this.querySelectorAll(selector).forEach((target) => {
      target.hidden = !isVisible;
    });
  }

  toggleClass(selector, className, shouldAdd) {
    this.querySelectorAll(selector).forEach((target) => {
      target.classList.toggle(className, shouldAdd);
    });
  }

  formatSignedMoney(cents) {
    if (!cents) return this.formatMoney(0);
    return `-${this.formatMoney(cents)}`;
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
    const value = Math.abs(Number(cents) || 0);
    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(value / 100));
    const amountWithComma = amount.replace('.', ',');
    const amountNoDecimalsWithComma = amountNoDecimals.replace('.', ',');

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsWithComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  updateDebug(state, visibility) {
    if (this.dataset.fpDebug !== 'true') return;

    const target = this.querySelector('[data-fp-summary-debug]');
    if (!target) return;

    target.textContent = [
      `status=${visibility}`,
      `variant=${state.variantId || 'none'}`,
      `width=${state.widthReady ? 'ok' : 'pending'}`,
      `height=${state.heightReady ? 'ok' : 'pending'}`,
      `price_variant=${this.variant.id || 'none'}`,
    ].join(' | ');
  }
}

if (!customElements.get('fp-product-purchase-summary')) {
  customElements.define('fp-product-purchase-summary', FpProductPurchaseSummary);
}
